import eventHero from '../../assets/images_of_victoria/IMG_2253.jpg';
import eventSupport from '../../assets/bio/Melanie-Ray-Photo.jpg';
import type { EventTicketType, PublicEvent } from '../types';

export const eventHeroImageOptions = [
  {
    id: 'victoria-outdoor',
    label: 'Victoria Outdoor Portrait',
    image: eventHero,
  },
  {
    id: 'community-support',
    label: 'Melanie and Victoria',
    image: eventSupport,
  },
] as const;

function priceLabel(
  price: number | null,
  ticket?: Pick<EventTicketType, 'category' | 'participantType'>,
): string {
  if (price === null) {
    if (
      ticket?.category === 'participant' &&
      (ticket.participantType === 'adult-model' || ticket.participantType === 'child-model')
    ) {
      return 'No fee';
    }

    return 'Coming Soon';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(price);
}

function normalizeLookupValue(value: unknown) {
  return String(value ?? '').trim().toLowerCase().replace(/[_\s]+/g, '-');
}

function normalizeParticipantType(value: unknown): 'general' | 'adult-model' | 'child-model' {
  const normalized = normalizeLookupValue(value);
  if (normalized === 'adult-model') {
    return 'adult-model';
  }
  if (normalized === 'child-model') {
    return 'child-model';
  }
  return 'general';
}

function inferTicketStatus(ticket: Pick<EventTicketType, 'status' | 'price' | 'category' | 'participantType' | 'name' | 'description'>): EventTicketType['status'] {
  const normalizedStatus = normalizeLookupValue(ticket.status);
  if (normalizedStatus === 'closed') {
    return 'closed';
  }

  if (normalizedStatus === 'open') {
    return 'open';
  }

  if (ticket.price !== null && ticket.price > 0) {
    return 'open';
  }

  const participantType = normalizeParticipantType(ticket.participantType);
  const normalizedName = normalizeLookupValue(ticket.name);
  const normalizedDescription = normalizeLookupValue(ticket.description);
  if (
    ticket.category === 'participant' &&
    (participantType === 'adult-model' ||
      participantType === 'child-model' ||
      normalizedName.includes('adult-model') ||
      normalizedName.includes('child-model') ||
      normalizedDescription.includes('adult-model') ||
      normalizedDescription.includes('child-model'))
  ) {
    return 'open';
  }

  return normalizedStatus === 'coming-soon' ? 'coming-soon' : 'coming-soon';
}

function buildTicket(ticket: Omit<EventTicketType, 'priceLabel'>): EventTicketType {
  const participantType =
    ticket.category === 'participant'
      ? normalizeParticipantType(ticket.participantType)
      : undefined;
  const status = inferTicketStatus({
    ...ticket,
    participantType: participantType ?? 'general',
  });

  return {
    ...ticket,
    status,
    participantType,
    priceLabel: priceLabel(ticket.price, {
      ...ticket,
      participantType: participantType ?? 'general',
    }),
  };
}

export function formatEventDateLabel(eventDate?: string): string {
  const value = String(eventDate || '').trim();
  if (!value) {
    return 'Date to be announced';
  }

  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return 'Date to be announced';
  }

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

export function formatEventTimeLabel(eventTime?: string): string {
  const value = String(eventTime || '').trim();
  if (!value) {
    return 'Time to be announced';
  }

  const [hoursText, minutesText = '00'] = value.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minutesText);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return 'Time to be announced';
  }

  const parsed = new Date();
  parsed.setHours(hours, minutes, 0, 0);

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

export const seededPublicEvents: PublicEvent[] = [
  {
    id: 'fall-extravaganza-fashion-show',
    slug: 'victorias-place-5th-annual-fall-extravaganza-fashion-show',
    title: "Victoria's Place 5th Annual Fall Extravaganza Fashion Show",
    shortDescription:
      'Bringing awareness to all children with disabilities.',
    fullDescription:
      'A fashion show with a purpose. Bringing awareness to all children with disabilities.',
    eventDate: '2026-09-19',
    eventTime: '16:00',
    dateLabel: 'Saturday, September 19, 2026',
    timeLabel: '4:00 PM',
    locationName: 'The Medallion Conference Center',
    locationAddress: '7309 Garners Ferry Road Columbia, SC 29209',
    status: 'open',
    isPublished: true,
    heroImage: eventHero,
    galleryImages: [eventHero, eventSupport],
    featured: true,
    tickets: [
      buildTicket({
        id: 'general-admission',
        name: 'General Admission',
        category: 'general',
        price: 25,
        description: 'Admission for guests attending the event and supporting the mission.',
        status: 'open',
      }),
      buildTicket({
        id: 'vendor-ticket',
        name: 'Vendor Ticket',
        category: 'vendor',
        price: 45,
        description: 'Reserved vendor participation for businesses joining the event experience.',
        status: 'open',
      }),
      buildTicket({
        id: 'resource-vendor-ticket',
        name: 'Resource Vendor',
        category: 'resource-vendor',
        price: 10,
        description: 'Community resource organizations with a dedicated presence at the event.',
        status: 'open',
      }),
      buildTicket({
        id: 'participant-registration',
        name: 'Adult Model Registration',
        category: 'participant',
        participantType: 'adult-model',
        price: null,
        description: 'Adult model application attached to this event. Approval is handled through the portal and no payment is required.',
        status: 'open',
      }),
      buildTicket({
        id: 'child-model-registration',
        name: 'Child Model Registration',
        category: 'participant',
        participantType: 'child-model',
        price: null,
        description: 'Child model registration is parent-led, attached to this event, and does not require payment.',
        status: 'open',
      }),
    ],
    highlights: [
      'General admission and vendor pricing are managed in one event setup.',
      'Every event action belongs to this published event page.',
      'Families, guests, and vendors all start from one clear destination.',
    ],
  },
  {
    id: 'family-community-day',
    slug: 'family-community-day',
    title: 'Family Community Day',
    shortDescription:
      'A family-centered gathering focused on connection, encouragement, and community support.',
    fullDescription:
      "Family Community Day is a welcoming event concept for children, families, volunteers, and partners to gather around encouragement, resources, and relationship-building. It is structured to be easier to publish, manage, and promote from the site's central events hub.",
    eventDate: '',
    eventTime: '',
    dateLabel: 'Coming Soon',
    timeLabel: 'Schedule to be announced',
    locationName: "Victoria's Place Community Event",
    locationAddress: 'Columbia, South Carolina',
    status: 'coming-soon',
    isPublished: false,
    heroImage: eventSupport,
    galleryImages: [eventSupport, eventHero],
    featured: false,
    tickets: [
      buildTicket({
        id: 'general-admission-community',
        name: 'General Admission',
        category: 'general',
        price: null,
        description: 'Future guest ticketing for families and community supporters.',
        status: 'coming-soon',
      }),
      buildTicket({
        id: 'vendor-community',
        name: 'Vendor Ticket',
        category: 'vendor',
        price: null,
        description: 'Future vendor participation connected directly to this event.',
        status: 'coming-soon',
      }),
    ],
    highlights: [
      'Built for family-centered connection and visibility.',
      'Easy to publish when details are finalized.',
      'Can later move to gallery highlights after the event.',
    ],
  },
];

export function slugifyEventTitle(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function formatTicketPrice(
  price: number | null,
  ticket?: Pick<EventTicketType, 'category' | 'participantType'>,
): string {
  return priceLabel(price, ticket);
}

export function normalizeEvent(event: PublicEvent): PublicEvent {
  const galleryImages = event.galleryImages.filter(Boolean);
  const heroImage = event.heroImage || galleryImages[0] || '';
  const eventDate = String(event.eventDate || '').trim();
  const eventTime = String(event.eventTime || '').trim();

  return {
    ...event,
    eventDate,
    eventTime,
    slug: slugifyEventTitle(event.slug || event.title) || event.id,
    dateLabel: eventDate ? formatEventDateLabel(eventDate) : (event.dateLabel || 'Date to be announced'),
    timeLabel: eventTime ? formatEventTimeLabel(eventTime) : (event.timeLabel || 'Time to be announced'),
    heroImage,
    galleryImages: galleryImages.length ? galleryImages : [heroImage].filter(Boolean),
    tickets: event.tickets.map((ticket) => ({
      ...ticket,
      status: inferTicketStatus(ticket),
      participantType:
        ticket.category === 'participant'
          ? normalizeParticipantType(ticket.participantType)
          : undefined,
      priceLabel: priceLabel(ticket.price, {
        ...ticket,
        participantType:
          ticket.category === 'participant'
            ? normalizeParticipantType(ticket.participantType)
            : undefined,
      }),
    })),
  };
}

export function normalizeEvents(events: PublicEvent[]): PublicEvent[] {
  const normalized = events.map(normalizeEvent);

  const featuredPublishedEvent = normalized.find((event) => event.isPublished && event.featured);

  if (featuredPublishedEvent) {
    return normalized.map((event) => ({
      ...event,
      featured: event.id === featuredPublishedEvent.id,
    }));
  }

  const firstPublishedEvent = normalized.find((event) => event.isPublished);
  if (!firstPublishedEvent) {
    return normalized.map((event) => ({
      ...event,
      featured: false,
    }));
  }

  return normalized.map((event) => ({
    ...event,
    featured: event.id === firstPublishedEvent.id,
  }));
}

export function createEmptyEvent(): PublicEvent {
  const timestamp = Date.now().toString(36);

  return normalizeEvent({
    id: `event-${timestamp}`,
    slug: `new-event-${timestamp}`,
    title: 'New Victoria’s Place Event',
    shortDescription: 'A new event is being prepared for Victoria’s Place families, supporters, and partners.',
    fullDescription:
      'Use the admin workspace to add the event story, set the ticket prices, and publish the event when it is ready.',
    eventDate: '',
    eventTime: '',
    dateLabel: 'Date to be announced',
    timeLabel: 'Time to be announced',
    locationName: "Victoria's Place Event Venue",
    locationAddress: 'Columbia, South Carolina',
    status: 'coming-soon',
    isPublished: false,
    heroImage: '',
    galleryImages: [],
    featured: false,
    tickets: [
      buildTicket({
        id: `general-${timestamp}`,
        name: 'General Admission',
        category: 'general',
        price: null,
        description: 'Admission for guests attending the event.',
        status: 'coming-soon',
      }),
      buildTicket({
        id: `vendor-${timestamp}`,
        name: 'Vendor Ticket',
        category: 'vendor',
        price: null,
        description: 'Vendor participation attached to this event.',
        status: 'coming-soon',
      }),
      buildTicket({
        id: `adult-model-${timestamp}`,
        name: 'Adult Model Registration',
        category: 'participant',
        participantType: 'adult-model',
        price: null,
        description: 'Adult model applications stay tied to this event and do not require payment.',
        status: 'coming-soon',
      }),
      buildTicket({
        id: `child-model-${timestamp}`,
        name: 'Child Model Registration',
        category: 'participant',
        participantType: 'child-model',
        price: null,
        description: 'Child model applications are parent-led, tied to this event, and do not require payment.',
        status: 'coming-soon',
      }),
    ],
    highlights: [
      'Published from one admin workflow.',
      'Ticket types stay attached to the event page.',
      'Families, vendors, and supporters see one clear destination.',
    ],
  });
}
